import React from 'react';
import Layout from '../../../components/Layout';
import Page from '../../../components/Page';
import fetch from '../../../core/fetch';

/**
 * Help Page Route Handler
 * 
 * Uses the new DynamicContent system via GraphQL for i18n.
 * Falls back to StaticPage if DynamicContent has no data.
 * Includes FAQ Schema for SEO.
 */

// Helper to normalize locale to short code
const getShortLocale = (locale) => {
  if (!locale) return 'es';
  return locale.substring(0, 2).toLowerCase();
};

// Helper to get localized field (fallback for StaticPage format)
const getLocalizedField = (data, field, locale) => {
  if (!data) return '';
  const code = getShortLocale(locale);
  if (code === 'es') return data[field] || '';
  const localizedField = `${field}_${code}`;
  return data[localizedField] || data[field] || '';
};

// Query for DynamicContent (new system)
const dynamicContentQuery = `query getDynamicContent($contentType: String!, $contentKey: String!, $locale: String!) {
  getDynamicContent(contentType: $contentType, contentKey: $contentKey, locale: $locale) {
    id
    content
    metaTitle
    metaDescription
    locale
  }
}`;

// Fallback query for StaticPage (legacy system)
const staticPageQuery = `query getEditStaticPage($id: Int!) {
  getEditStaticPage(id: $id) {
    id
    pageName
    content
    content_en
    content_fr
    content_it
    content_pt
    metaTitle
    metaTitle_en
    metaTitle_fr
    metaTitle_it
    metaTitle_pt
    metaDescription
    metaDescription_en
    metaDescription_fr
    metaDescription_it
    metaDescription_pt
  }
}`;

// SEO: FAQPage Schema for Help page
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "¿Cómo reservar un bote en Navega?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Busca tu destino, selecciona las fechas, elige el bote que más te guste y completa el proceso de reserva con pago seguro."
      }
    },
    {
      "@type": "Question",
      "name": "¿Qué métodos de pago acepta Navega?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Aceptamos tarjetas de crédito/débito (Visa, Mastercard, American Express), Pago Móvil, C2P y transferencias bancarias en Venezuela."
      }
    },
    {
      "@type": "Question",
      "name": "¿Los botes incluyen capitán?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Depende del bote. Algunos incluyen capitán certificado, otros permiten alquiler sin capitán si tienes licencia de navegación."
      }
    },
    {
      "@type": "Question",
      "name": "¿Cómo contacto al dueño del bote?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Una vez realizada la reserva, podrás comunicarte directamente con el dueño a través del sistema de mensajería de Navega."
      }
    },
    {
      "@type": "Question",
      "name": "¿Cuál es la política de cancelación?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cada bote tiene su propia política de cancelación (Flexible, Moderada o Estricta). Revisa los detalles antes de reservar."
      }
    }
  ]
};

export default async function action({ locale }) {
  try {
    // First try the new DynamicContent system
    const dynamicResp = await fetch('/graphql', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: dynamicContentQuery,
        variables: {
          contentType: 'static_page',
          contentKey: 'help',
          locale: locale || 'es'
        },
      }),
      credentials: 'include',
    });

    const dynamicData = await dynamicResp.json();
    const dynamicContent = dynamicData?.data?.getDynamicContent;

    // If DynamicContent has data, use it
    if (dynamicContent && dynamicContent.content) {
      return {
        title: dynamicContent.metaTitle || 'Help',
        description: dynamicContent.metaDescription || '',
        chunk: 'help',
        jsonLd: [faqSchema],
        component: <Layout><Page html={dynamicContent.content} title={dynamicContent.metaTitle} /></Layout>,
      };
    }

    // Fallback to StaticPage (legacy system)
    const staticResp = await fetch('/graphql', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: staticPageQuery,
        variables: { id: 5 },
      }),
      credentials: 'include',
    });

    const staticData = await staticResp.json();
    const page = staticData?.data?.getEditStaticPage;

    if (!page) {
      return undefined;
    }

    // Get localized content from StaticPage
    const title = getLocalizedField(page, 'metaTitle', locale);
    const description = getLocalizedField(page, 'metaDescription', locale);
    const content = getLocalizedField(page, 'content', locale);

    return {
      title: title || 'Help',
      description,
      chunk: 'help',
      jsonLd: [faqSchema],
      component: <Layout><Page html={content} title={title} /></Layout>,
    };
  } catch (error) {
    console.error('[Help Page] Error loading content:', error);

    return {
      title: 'Help',
      description: '',
      chunk: 'help',
      jsonLd: [faqSchema],
      component: <Layout><Page html="<p>Error loading content</p>" title="Help" /></Layout>,
    };
  }
}
